/**
 * Client per gli endpoint `/ai/search`, `/ai/recommender`, `/ai/feedback`.
 */
import { axiosInstance } from './apiClient';

export type AiSearchEntityType = 'energy_use' | 'intervention' | 'indicator';

export type AiItemFilter = 'interventi' | 'indicatori' | 'uses';

export type AiSourceTipo = 'internal' | 'external';

/** Risposta grezza API (SearchResultItem). */
export type AiApiSearchResultItem = {
  uid: string;
  id_source: string;
  tipo: AiSourceTipo;
  fonte: string;
  fields: Record<string, unknown>;
};

/** Item normalizzato per UI (modale, import). */
export type AiSearchResultItem = {
  uid: string;
  id_source: string;
  tipo: AiSourceTipo;
  fonte: string;
  entityType: AiSearchEntityType;
  payload: Record<string, unknown>;
  /** Alias retrocompatibilità liste mock */
  id: string;
};

export type AiSearchRequest = {
  entityTypes: AiSearchEntityType[];
  query: string;
  id_entity?: string | null;
  include_external?: boolean;
  top_k?: number;
};

const ENTITY_TO_FILTER: Record<AiSearchEntityType, AiItemFilter> = {
  energy_use: 'uses',
  intervention: 'interventi',
  indicator: 'indicatori',
};

export function entityTypeToFilter(entityType: AiSearchEntityType): AiItemFilter {
  return ENTITY_TO_FILTER[entityType];
}

function mapApiResultToUiItem(
  raw: AiApiSearchResultItem,
  entityType: AiSearchEntityType
): AiSearchResultItem {
  return {
    uid: raw.uid,
    id_source: raw.id_source,
    tipo: raw.tipo,
    fonte: raw.fonte,
    entityType,
    payload: { ...raw.fields, fonte: raw.fonte },
    id: raw.uid,
  };
}

function aiErrorMessage(err: unknown): string {
  const ax = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
  if (ax.response?.status === 503) {
    return 'Servizio AI non disponibile. Verificare la configurazione del backend.';
  }
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return ax.message || 'Errore durante la richiesta AI';
}

export async function postAiSearch(params: {
  query: string;
  filter: AiItemFilter;
  id_entity?: string | null;
  include_external?: boolean;
  top_k?: number;
}): Promise<AiApiSearchResultItem[]> {
  const { data } = await axiosInstance.post<AiApiSearchResultItem[]>('/ai/search', {
    query: params.query,
    filter: params.filter,
    id_entity: params.id_entity ?? null,
    include_external: params.include_external ?? true,
    top_k: params.top_k,
  });
  return data ?? [];
}

export async function postAiRecommender(params: {
  id_entity: string;
  filter: AiItemFilter;
  include_external?: boolean;
  top_k?: number;
}): Promise<AiApiSearchResultItem[]> {
  const { data } = await axiosInstance.post<AiApiSearchResultItem[]>('/ai/recommender', {
    id_entity: params.id_entity,
    filter: params.filter,
    include_external: params.include_external ?? true,
    top_k: params.top_k,
  });
  return data ?? [];
}

export async function submitAiFeedback(uid: string, signal: 1 | -1 = 1): Promise<void> {
  await axiosInstance.post('/ai/feedback', { uid, signal });
}

/** Ricerca libera: una chiamata `/ai/search` per ogni tipo selezionato. */
export async function fetchAiSearch(params: AiSearchRequest): Promise<AiSearchResultItem[]> {
  const q = params.query.trim();
  if (!q || !params.entityTypes.length) return [];

  const includeExternal = params.include_external ?? true;
  const batches = await Promise.all(
    params.entityTypes.map(async entityType => {
      try {
        const raw = await postAiSearch({
          query: q,
          filter: entityTypeToFilter(entityType),
          id_entity: params.id_entity,
          include_external: includeExternal,
          top_k: params.top_k,
        });
        return raw.map(r => mapApiResultToUiItem(r, entityType));
      } catch (e) {
        throw new Error(aiErrorMessage(e));
      }
    })
  );
  return batches.flat();
}

export async function fetchAiRecommender(
  diagnosisId: string,
  entityType: AiSearchEntityType,
  includeExternal = true,
  topK = 30
): Promise<AiSearchResultItem[]> {
  try {
    const raw = await postAiRecommender({
      id_entity: diagnosisId,
      filter: entityTypeToFilter(entityType),
      include_external: includeExternal,
      top_k: topK,
    });
    return raw.map(r => mapApiResultToUiItem(r, entityType));
  } catch (e) {
    throw new Error(aiErrorMessage(e));
  }
}

export const AI_SEARCH_ENTITY_OPTIONS: { value: AiSearchEntityType; label: string }[] = [
  { value: 'energy_use', label: 'Usi energetici' },
  { value: 'intervention', label: 'Interventi' },
  { value: 'indicator', label: 'Indicatori' },
];

export const AI_SEARCH_ENTITY_LABELS: Record<AiSearchEntityType, string> = {
  energy_use: 'Usi energetici',
  intervention: 'Interventi',
  indicator: 'Indicatori',
};
